import React, { createContext, useContext, useMemo } from 'react'
import ErrorIcon from '../../../components/icons/ErrorIcon'
import { Typography } from '../Typography'
import { ErrorText, Fields, GroupRoot, HintText, Legend, RequiredMark } from './InputGroup.styles'
import type { InputGroupContextValue, InputGroupProps } from './InputGroup.types'
import { InputGroupField } from './InputGroupField'

export const InputGroupContext = createContext<InputGroupContextValue | null>(null)

export const useInputGroupContext = () => {
  const context = useContext(InputGroupContext)
  if (!context) {
    throw new Error('useInputGroupContext must be used within an InputGroup')
  }
  return context
}

const InputGroupComponent: React.FC<InputGroupProps> = ({
  label,
  error,
  hint,
  required,
  disabled,
  columns,
  gap,
  layout = 'horizontal',
  children,
  ...props
}) => {
  const groupId = React.useId()
  const errorId = error ? `${groupId}-error` : undefined
  const hintId = hint ? `${groupId}-hint` : undefined

  const contextValue = useMemo<InputGroupContextValue>(
    () => ({
      groupId,
      disabled,
      required,
      errorId,
      hintId,
    }),
    [groupId, disabled, required, errorId, hintId]
  )

  return (
    <InputGroupContext.Provider value={contextValue}>
      <GroupRoot $disabled={disabled} {...props}>
        <Legend>
          <Typography variant="Heading/Heading-14-Semibold" color="label">
            {label}
            {required && <RequiredMark>*</RequiredMark>}
          </Typography>
        </Legend>

        <Fields $columns={columns} $gap={gap} $layout={layout}>
          {children}
        </Fields>

        {hint && !error && (
          <HintText id={hintId} variant="Heading/Heading-14-medium" color="textSecondary">
            {hint}
          </HintText>
        )}

        {error && (
          <ErrorText id={errorId} variant="Heading/Heading-14-medium" color="danger">
            <ErrorIcon />
            {error}
          </ErrorText>
        )}
      </GroupRoot>
    </InputGroupContext.Provider>
  )
}

export const InputGroup = Object.assign(InputGroupComponent, {
  Field: InputGroupField,
})
